// Credential-bearing query parameters, read back after the head guard took
// them out of the URL.
//
// THE CONTRACT WITH THE GUARD
// apps/web/public/token-guard.js runs first in <head>, above the GTM
// bootstrap. For each name below it moves `?<name>=<value>` out of the
// address bar and parks the value in sessionStorage under
// `zoe.handoff.<name>`. Nothing after it — GTM's `dl=`, the analytics landing
// events, error reporting, any third-party script — ever sees the value in a
// URL. The two sides must agree on these key names.
//
// A value is lifted into module memory on its first read and deleted from
// sessionStorage there and then: it survives React StrictMode's double mount
// and an unmount/remount of the screen, but not a page reload, and it is
// never left sitting in storage for the next visit to the same tab.

export type HandoffCredential = 'session' | 'handoff' | 'challenge';

const STORAGE_PREFIX = 'zoe.handoff.';

/** See THE CONTRACT WITH THE GUARD above — token-guard.js builds this same key. */
export function handoffCredentialStorageKey(name: HandoffCredential): string {
  return `${STORAGE_PREFIX}${name}`;
}

const claimed = new Map<HandoffCredential, string>();

/**
 * The value the guard parked for `name`, or '' when there was none.
 *
 * Idempotent: every later call in this page's life answers the same value.
 */
export function readHandoffCredential(name: HandoffCredential): string {
  const already = claimed.get(name);
  if (already !== undefined) return already;
  if (typeof window === 'undefined') return '';

  let parked = '';
  try {
    parked = window.sessionStorage.getItem(handoffCredentialStorageKey(name)) ?? '';
    window.sessionStorage.removeItem(handoffCredentialStorageKey(name));
  } catch {
    // Storage blocked (private window, site-data policy) — nothing to claim.
  }
  claimed.set(name, parked);
  return parked;
}

/**
 * Forget every parked credential. Called when this browser's session
 * credential changes (sign-out, account delete, a rejected session) so a
 * later visit to /signin in the same tab cannot resurrect a dead hand-off.
 */
export function clearHandoffCredentials(): void {
  claimed.clear();
  if (typeof window === 'undefined') return;
  for (const name of ['session', 'handoff', 'challenge'] as const) {
    try {
      window.sessionStorage.removeItem(handoffCredentialStorageKey(name));
    } catch {
      // ignore
    }
  }
}
